import type { CSSProperties } from 'react';
import type { Column } from '@tanstack/react-table';
import type { TableLayout, TableRowData } from '../types';

/** TanStack's default for an unset maxSize — treat as "no cap". */
const UNSET_MAX_SIZE = Number.MAX_SAFE_INTEGER;

export interface ColumnSize {
  size: number;
  minSize: number;
  maxSize: number;
}

export function readColumnSize<TRow extends TableRowData>(
  column: Column<TRow, unknown>
): ColumnSize {
  return {
    size: column.getSize(),
    minSize: column.columnDef.minSize ?? 0,
    maxSize: column.columnDef.maxSize ?? UNSET_MAX_SIZE,
  };
}

/**
 * Per-cell sizing style by layout.
 *
 * `fixed` pins an authoritative `width`. In `auto`, `max-width` is unreliable on
 * table cells, so a column's `maxWidth` is used only as the fixed/grow flag while
 * `min-width` (honored on cells) is always emitted as the floor:
 * - capped column (has `maxWidth`) → fixed `width` that holds.
 * - uncapped column → greedy `width: 100%` that absorbs surplus.
 *
 * The always-on `min-width` floor stops grow columns from squeezing fixed ones
 * below their minimum.
 */
export function buildColumnSizeStyle(
  layout: TableLayout,
  { size, minSize, maxSize }: ColumnSize
): CSSProperties {
  if (layout !== 'auto') {
    return { width: size };
  }

  const style: CSSProperties = {
    width: maxSize < UNSET_MAX_SIZE ? size : '100%',
  };
  if (minSize > 0) {
    style.minWidth = minSize;
  }
  return style;
}
